import React, { Component } from 'react';
import { debounce } from 'throttle-debounce';
import PropTypes from 'prop-types';
import escapeStringRegex from 'escape-string-regexp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentAlt } from '@fortawesome/free-solid-svg-icons/faCommentAlt';
import { faAlignLeft } from '@fortawesome/free-solid-svg-icons/faAlignLeft';

export default class BillAnnotationFromSelection extends Component {
  constructor(props) {
    super(props);

    this.annotationBoundaries = null;
    this.annotationsIsEditable = true;

    this.debouncedHandleSelectionChange = debounce(30, this.handleSelectionChange);

    this.state = {
      annotationLinkVisible: false,
      annotationLinkTopPos: 0,
      annotationLinkLeftPos: 0,
    };
  }

  componentDidMount() {
    document.addEventListener('selectionchange', this.debouncedHandleSelectionChange);
    this.rootNode = document.getElementById('billText');
  }

  componentWillUnmount() {
    document.removeEventListener(
      'selectionchange',
      this.debouncedHandleSelectionChange
    );
  }

  showAnnotationLink = (position) => {
    this.setState({
      annotationLinkVisible: true,
      annotationLinkTopPos: position.top - 24,
      annotationLinkLeftPos: position.left,
    });
  };

  hideAnnotationLink = () => {
    if (this.state.annotationLinkVisible) {
      this.setState({
        annotationLinkVisible: false,
      });
    }
  };

  editAnnotation = this.annotationBoundaries
    ? {
        annotation: {
          fromLine: `${this.annotationBoundaries.startLine}`, // API requires this be a string
          fromCharacter: this.annotationBoundaries.startOffset,
          toLine: `${this.annotationBoundaries.endLine}`, // API requires this be a string
          toCharacter: this.annotationBoundaries.endOffset,
          selectedText: this.annotationBoundaries.selectedText,
        },
        billData: {
          billId: this.props.billId,
          billVersionId: this.props.billVersionId,
        },
      }
    : undefined;

  addAnnotation = (event) => {
    if (!this.range) return;

    this.annotationBoundaries = this.findBoundaries(this.range);
    this.setState({
      annotationLinkVisible: false,
    });
    this.props.openEditUserActionModal(this.editAnnotation);
  };

  handleSelectionChange = (event) => {
    this.getSelection();

    if (!this.range || !this.annotationsIsEditable) {
      this.hideAnnotationLink();
    } else if (!!this.range && this.annotationsIsEditable) {
      const parentIds = BillAnnotationFromSelection.getParentIds(
        this.range.commonAncestorContainer
      );
      if (parentIds.includes('billText')) {
        const position = this.getLinkPosition(this.range);
        this.showAnnotationLink(position);
      }
    }
  };

  static getParentIds(node) {
    const parents = [];
    while (node) {
      if (node.id) {
        parents.unshift(node.id);
      }
      node = node.parentNode;
    }

    return parents;
  }

  getLinkPosition = (element) => {
    const domElement = document.getElementById('billView').getBoundingClientRect();
    const position = element.getBoundingClientRect();
    return {
      top: position.top - domElement.top - 10,
      left: position.left - domElement.left + position.width / 2,
    };
  };

  getSelection = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) {
      return;
    }
    const range = selection.getRangeAt(0);
    this.range = range.collapsed ? null : range;
  };

  getSelectionText = () => {
    let text = '';
    if (window.getSelection) {
      text = window.getSelection().toString();
    } else if (document.selection && document.selection.type !== 'Control') {
      text = document.selection.createRange().text;
    }
    return text;
  };

  /**
   * Identify how much to adjust a position within text, based on map of deletions in that text
   * @param pos
   * @param deltaMap
   * @returns {*}
   */
  sumDeletionDelta = (pos, deltaMap) => {
    return deltaMap.reduce((sum, delta) => {
      return delta.pos < pos ? sum + delta.length : sum;
    }, 0);
  };

  findSelectedTextInMarkdownDocument(selectedText, markdown) {
    const {
      diffTags: { added, removed },
    } = this.props;

    // Sometimes markdown is a diff (hence ===/~~), and matching HTMl-selected text to positions within source HTML
    // is problematic! so replace any HTML (just ===/~~) so are we searching for text match within a clean document
    const insDelRegex = new RegExp(
      `${added.start}|${added.end}|${removed.start}|${removed.end}`,
      'gi'
    );

    // but we need to create a map of the positions within the source doc to the clean doc, based on what was deleted
    // so we can transform from position in clean to position in source (annotation should be "position in source")
    let match = null;
    const deletionDeltaMap = [];
    let runningDeletionTotal = 0;
    while ((match = insDelRegex.exec(markdown))) {
      deletionDeltaMap.push({
        pos: match.index - runningDeletionTotal,
        length: insDelRegex.lastIndex - match.index,
      });
      runningDeletionTotal += insDelRegex.lastIndex - match.index;
    }
    markdown = markdown.replace(insDelRegex, '');

    // Regex to match *just* the selected "words"/text within the full, displayed text
    // anywhere that starts with whitespace (between "words") might also contain within the markdown:
    // === ~~ * - # _ ~
    // ===/~~       = (?:<\/?(===|~~)>)?
    // *-#_~>       = (?:[*\-#_~>]+)?
    // whitespace   = \s+
    // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
    const selectionRegex = new RegExp(
      escapeStringRegex(selectedText).replace(
        /\s+/g,
        `(?:${added.start}|${added.end}|${removed.start}|${removed.end}|[*\\-#_~>\\s])+`
      ),
      'g'
    );

    const matches = [];
    while ((match = selectionRegex.exec(markdown))) {
      // Adjust positions on the match based on the deletionDeltaMap, above
      matches.push({
        start: match.index + this.sumDeletionDelta(match.index, deletionDeltaMap),
        end:
          selectionRegex.lastIndex
          + this.sumDeletionDelta(selectionRegex.lastIndex, deletionDeltaMap),
        selectionInDisplayText: match[0],
      });
    }

    return matches;
  }

  findBoundaries = (range) => {
    const selectedText = this.getSelectionText();
    const matches = this.findSelectedTextInMarkdownDocument(
      selectedText,
      this.props.sourceBillText
    );

    if (matches.length === 0) {
      console.error(
        'Failed to find matching string in sourceBillText for selection',
        selectedText
      );
      return {
        startLine: 0,
        startOffset: 0,
        endLine: 0,
        endOffset: 0,
        selectedText,
      };
    }

    const correctMatch = this.selectBestMatch(matches, range);

    return {
      startLine: BillAnnotationFromSelection.getLineNumberMatchingStringIndex(
        this.props.sourceBillText,
        correctMatch.start
      ),
      startOffset: correctMatch.start, // NOTE! this is now the absolute index in the string, not the line
      endLine: BillAnnotationFromSelection.getLineNumberMatchingStringIndex(
        this.props.sourceBillText,
        correctMatch.end
      ),
      endOffset: correctMatch.end, // NOTE! this is now the absolute index in the string, not the line
      selectedText,
    };
  };

  addSiblingTextUntilMatchInBillText(text, element) {
    const matches = this.findSelectedTextInMarkdownDocument(
      text,
      this.props.sourceBillText
    );
    if (matches.length < 2 || element.nextElementSibling === null) {
      return matches;
    }

    return this.addSiblingTextUntilMatchInBillText(
      text + element.nextElementSibling.textContent,
      element.nextElementSibling
    );
  }

  selectBestMatch(matches, range) {
    // Worst case scenario, return first match
    let correctMatch = matches[0];

    // IF MORE THAN ONE MATCH (ie, short, generic selection) then use selection context to find right one
    // if startContainer.type === text then get startContainer.data
    //    find that text within orig document to get containing bounds of selected text
    // if startContainer.data === endContainer.data, then text is within startContainer
    // otherwise
    //    find text of startContainer and endContainer in orig doc, get start and end bounds, text is within that
    if (matches.length > 1) {
      // More than one matching string in original document
      // So try to find best context from combination of range.startContainer and endContainer
      let contextStartPos = null;
      let contextEndPos = null;
      let contextToSelectionOffset = null;

      const containerMatches = this.findSelectedTextInMarkdownDocument(
        range.startContainer.textContent,
        this.props.sourceBillText
      );
      if (containerMatches.length > 0) {
        contextStartPos = containerMatches[0].start;
        contextEndPos = containerMatches[0].end;
        contextToSelectionOffset = containerMatches[0].start + range.startOffset;
      }

      // Selection text stretches across containers
      if (range.startContainer.textContent !== range.endContainer.textContent) {
        const endContainerMatches = this.addSiblingTextUntilMatchInBillText(
          range.endContainer.textContent,
          range.endContainer
        );
        if (endContainerMatches.length === 1) {
          contextEndPos = endContainerMatches[0].end;
        } else {
          console.error(
            'got bad number of matches for end container',
            endContainerMatches
          );
        }
      }

      // Use context to find the best match within it
      if (
        contextStartPos !== null
        && contextEndPos !== null
        && contextToSelectionOffset !== null
      ) {
        correctMatch = matches.reduce((bestMatch, match) => {
          if (match.start > contextStartPos && match.end < contextEndPos) {
            // might be a best match
            if (
              Math.abs(bestMatch.start - contextToSelectionOffset)
              > Math.abs(match.start - contextToSelectionOffset)
            ) {
              return match;
            }
          }
          return bestMatch;
        }, correctMatch);
      }
    }

    // worst case scenario, we just returned the first match
    return correctMatch;
  }

  static getLineNumberMatchingStringIndex(str, index, baseZero) {
    return str.substring(0, index).split('\n').length - (baseZero ? 1 : 0);
  }

  render() {
    const annotationLinkVisible = {
      opacity: this.state.annotationLinkVisible ? 1 : 0,
      zIndex: this.state.annotationLinkVisible ? 1000 : -1000,
      top: this.state.annotationLinkTopPos,
      left: this.state.annotationLinkLeftPos,
      color: '#f68c24',
    };

    return (
      <div>
        <div
          id="addAnnotation"
          style={annotationLinkVisible}
          className="fa-stack fa-3x"
        >
          <FontAwesomeIcon
            icon={faCommentAlt}
            className="fa-stack-1x"
            size="1x"
            onMouseDown={this.addAnnotation}
          >
            +
          </FontAwesomeIcon>
          <FontAwesomeIcon
            icon={faAlignLeft}
            className="fa-stack-2x pb-2 comment-bubble"
            size="2x"
            onMouseDown={this.addAnnotation}
          >
            +
          </FontAwesomeIcon>
        </div>
      </div>
    );
  }
}

BillAnnotationFromSelection.propTypes = {
  userSelectedTeams: PropTypes.array,
  openEditUserActionModal: PropTypes.func,
};
